/**
 * 번역 음성 재생 (참가자 화면 전용. 운영자 기기는 번역 음성을 재생하지 않는다).
 *
 * 수신 PCM16 24kHz 청크를 `sampleRate 24000`의 `AudioBuffer`로 만들어 컨텍스트가
 * 리샘플하게 하고, `nextStartTime`에 이어 붙여 스케줄한다.
 *
 * ## 드리프트 제어
 *   1. **언더런** — `nextStartTime < currentTime`이면 `currentTime + 0.1`로 재동기
 *   2. **지연 누적** — 선행량(`nextStartTime - currentTime`)이 1.5초를 넘으면
 *      가장 오래된 **미재생** 청크부터 버려 0.5초로 되돌리고 `drop`을 센다
 *   3. **따라잡기** — 선행량 0.8~1.5초 구간은 `playbackRate` 1.05로 재생
 *
 * 초기 버퍼는 200ms.
 */
import { OUTPUT_SAMPLE_RATE } from './protocol';
import { int16ToFloat32 } from './pcm';

/** 재생 시작 전 쌓아 두는 여유 */
const INITIAL_BUFFER_SEC = 0.2;
/** 언더런 재동기 시 앞으로 미는 양 */
const RESYNC_LEAD_SEC = 0.1;
/** 이 선행량을 넘으면 오래된 청크를 버린다 */
const MAX_LEAD_SEC = 1.5;
/** 버린 뒤 되돌릴 선행량 */
const TRIM_TARGET_SEC = 0.5;
/** 이 선행량을 넘으면 따라잡기 재생 */
const CATCHUP_LEAD_SEC = 0.8;
/** 따라잡기 배속 */
const CATCHUP_RATE = 1.05;

export interface PlayerStats {
  /** 현재 스케줄된 선행량(초) */
  leadSec: number;
  /** 관측한 최대 선행량(초) */
  maxLeadSec: number;
  /** 선행량 초과로 버린 청크 수 */
  drops: number;
  /** 언더런 재동기 횟수 */
  resyncs: number;
  /** 재생한 청크 수 */
  chunks: number;
  /** 재생한 오디오 길이 합(초) */
  playedSec: number;
}

interface Scheduled {
  node: AudioBufferSourceNode;
  buffer: AudioBuffer;
  /** 컨텍스트 시각 기준 시작 */
  start: number;
  /** 배속을 반영한 재생 길이 */
  duration: number;
}

export class TranslationPlayer {
  private ctx: AudioContext | null = null;
  private gain: GainNode | null = null;
  private nextStartTime = 0;
  private scheduled: Scheduled[] = [];

  private maxLeadSec = 0;
  private drops = 0;
  private resyncs = 0;
  private chunks = 0;
  private playedSec = 0;

  /** 사용자 제스처 안에서 호출해야 한다(모바일 자동재생 정책). */
  async ensureContext(): Promise<AudioContext> {
    // 뒤로 가기 캐시·전화 수신 뒤에는 컨텍스트가 닫혀 있을 수 있다 → 새로 만든다.
    if (this.ctx !== null && this.ctx.state === 'closed') {
      this.ctx = null;
      this.gain = null;
    }
    if (this.ctx === null) {
      // 샘플레이트는 기기 기본값을 쓴다. iOS는 24000 지정을 무시할 수 있다.
      this.ctx = new AudioContext();
      this.gain = this.ctx.createGain();
      this.gain.gain.value = 1;
      this.gain.connect(this.ctx.destination);
    }
    // iOS Safari는 'suspended' 외에 'interrupted'(비표준) 상태도 쓴다. running이 아니면 다시 켠다.
    if (this.ctx.state !== 'running') await this.ctx.resume();
    return this.ctx;
  }

  /**
   * PCM16 24kHz 청크를 재생 큐에 넣는다.
   *
   * 코어는 `enqueueFloat32`이고 이 메서드는 `int16ToFloat32` 변환만
   * 하는 얇은 래퍼다. 시그니처와 산출 결과는 전환 전과 같다(Float32 왕복 없음).
   */
  enqueue(pcm: Int16Array<ArrayBuffer>): void {
    this.enqueueFloat32(int16ToFloat32(pcm), OUTPUT_SAMPLE_RATE);
  }

  /**
   * 이미 Float32(-1~1)로 풀린 샘플을 재생 큐에 넣는다.
   *
   * `AudioDecoder`가 내놓는 `AudioData`는 `f32-planar`라 이 입구를 그대로 쓴다.
   * 드리프트·언더런 제어는 `buffer.duration` 기반이라 청크 길이·샘플레이트에 무관하다.
   * 샘플레이트가 기기 출력과 다르면 `AudioContext`가 리샘플한다.
   */
  enqueueFloat32(samples: Float32Array<ArrayBuffer>, sampleRate: number): void {
    const ctx = this.ctx;
    const gain = this.gain;
    if (ctx === null || gain === null || samples.length === 0) return;

    const buffer = ctx.createBuffer(1, samples.length, sampleRate);
    buffer.copyToChannel(samples, 0);

    const now = ctx.currentTime;
    this.prune(now);

    // (1) 언더런 재동기 — 큐가 말라 재생이 끊긴 상태
    if (this.nextStartTime < now + 0.01) {
      const wasRunning = this.nextStartTime > 0;
      this.nextStartTime = now + (wasRunning ? RESYNC_LEAD_SEC : INITIAL_BUFFER_SEC);
      if (wasRunning) {
        this.resyncs += 1;
      }
    }

    // (2) 지연 누적 — 오래된 미재생 청크를 버려 0.5초로 되돌린다
    if (this.nextStartTime - now > MAX_LEAD_SEC) this.trim(now);

    // (3) 따라잡기 배속
    const lead = this.nextStartTime - now;
    const rate = lead > CATCHUP_LEAD_SEC ? CATCHUP_RATE : 1;

    const node = ctx.createBufferSource();
    node.buffer = buffer;
    node.playbackRate.value = rate;
    node.connect(gain);
    const duration = buffer.duration / rate;
    node.start(this.nextStartTime);

    this.scheduled.push({ node, buffer, start: this.nextStartTime, duration });
    this.nextStartTime += duration;
    this.chunks += 1;
    this.playedSec += buffer.duration;

    const newLead = this.nextStartTime - now;
    if (newLead > this.maxLeadSec) this.maxLeadSec = newLead;
  }

  /**
   * 선행량이 한계를 넘었다. 아직 시작하지 않은 청크 중 **오래된 것부터** 버리고,
   * 남은 청크를 0.5초 뒤부터 다시 이어 붙인다.
   */
  private trim(now: number): void {
    const future: Scheduled[] = [];
    const kept: Scheduled[] = [];
    for (const s of this.scheduled) {
      if (s.start > now) future.push(s);
      else kept.push(s);
    }
    if (future.length === 0) {
      // 버릴 미재생 청크가 없다. 시작점만 당긴다.
      this.nextStartTime = now + TRIM_TARGET_SEC;
      return;
    }

    // 전부 멈춘 뒤 필요한 만큼만 다시 건다.
    for (const s of future) {
      try {
        s.node.stop();
      } catch {
        // 이미 끝난 노드
      }
      s.node.disconnect();
    }

    // 재생 중인 청크가 끝나는 시각 이후부터 다시 채운다.
    let resumeAt = now + TRIM_TARGET_SEC;
    for (const s of kept) resumeAt = Math.max(resumeAt, s.start + s.duration);

    const excess = this.nextStartTime - (now + TRIM_TARGET_SEC);
    let droppedSec = 0;
    let index = 0;
    while (index < future.length && droppedSec < excess) {
      droppedSec += future[index]?.duration ?? 0;
      index += 1;
      this.drops += 1;
    }

    const ctx = this.ctx;
    const gain = this.gain;
    this.scheduled = kept;
    this.nextStartTime = resumeAt;
    if (ctx !== null && gain !== null) {
      for (let i = index; i < future.length; i += 1) {
        const s = future[i];
        if (s === undefined) continue;
        const node = ctx.createBufferSource();
        node.buffer = s.buffer;
        node.playbackRate.value = CATCHUP_RATE;
        node.connect(gain);
        const duration = s.buffer.duration / CATCHUP_RATE;
        node.start(this.nextStartTime);
        this.scheduled.push({ node, buffer: s.buffer, start: this.nextStartTime, duration });
        this.nextStartTime += duration;
      }
    }
  }

  /** 이미 끝난 스케줄을 목록에서 지운다. */
  private prune(now: number): void {
    if (this.scheduled.length < 32) return;
    this.scheduled = this.scheduled.filter((s) => s.start + s.duration > now - 1);
  }

  /** 스케줄된 재생을 모두 버린다(세션 종료·flush). */
  reset(): void {
    for (const s of this.scheduled) {
      try {
        s.node.stop();
      } catch {
        // 이미 끝난 노드
      }
      s.node.disconnect();
    }
    this.scheduled = [];
    this.nextStartTime = 0;
  }

  stats(): PlayerStats {
    const now = this.ctx?.currentTime ?? 0;
    const leadSec = this.nextStartTime > now ? this.nextStartTime - now : 0;
    return {
      leadSec,
      maxLeadSec: this.maxLeadSec,
      drops: this.drops,
      resyncs: this.resyncs,
      chunks: this.chunks,
      playedSec: this.playedSec,
    };
  }

  async close(): Promise<void> {
    this.reset();
    const ctx = this.ctx;
    this.ctx = null;
    this.gain = null;
    if (ctx !== null) await ctx.close();
  }
}
